import {
  AdgroupFormData,
  AudienceEstimate,
  CampaignFormData,
  CreateMode,
  DpaLocalAudience,
  EstimatedType,
  EstimateStatus,
  NO_LIMIT,
} from '@tt4b/ttam-enums';
import {
  StateEnum,
  useAdgroupMode,
  useAllowlist,
  useAudienceEstiamte,
  useStdAccountStore,
  useStdAdgroupIs,
  useStdObjectiveType,
  useStore,
  useAdgroupIos14,
  useAudienceEstimateApi,
  useRequest,
  EstimateDataType,
  useCurrencyOption,
} from '@tt4b/ttam-hooks';

import { audienceRequstFormatter, AuEstFormmater, localizeNumber } from '@tt4b/ttam-utils';
import { Ref, ref, computed, watch, onMounted } from '@vue/composition-api';
import { debounce, isEqual } from 'lodash-es';
import { InventoryFlow } from '@ad/tt_ads_common_enums';
import { createSharedComposable } from '@vueuse/core';
import { formatNumberByLangCode } from '@tt4b/infra-utils';
const curEstiamteResult = ref<EstimateDataType>();

/**
 * 定向扩量提示
 */
export function usePropsTargetExpansionTipVisible(options: { adgroupData: Ref<AdgroupFormData> }) {
  const { adgroupData } = options;
  const targetExpansionTipVisible = computed(
    () =>
      adgroupData.value.targetingExpansion.expansion_enabled &&
      adgroupData.value.targetingExpansion.expansion_types.length > 0,
  );

  return {
    targetExpansionTipVisible,
  };
}

/**
 * 地域定向提醒显示，没有选择国家时候显示
 */
export function usePropsLocationTipVisible(options: { adgroupData: Ref<AdgroupFormData> }) {
  const { adgroupData } = options;
  const locationTipVisible = computed(() => {
    const { particleLocations, zipcodeIds } = adgroupData.value;
    return !particleLocations.length && !zipcodeIds.length;
  });

  return {
    locationTipVisible,
  };
}

/**
 * 包括 18 岁以下受众时候显示这个提示
 */
export function usePropsAgeTipVisible(options: {
  estimateType: Ref<EstimatedType>;
  adgroupData: Ref<AdgroupFormData>;
}) {
  const { estimateType, adgroupData } = options;
  const ageTipVisible = computed(() => {
    const { age } = adgroupData.value;
    const noData = estimateType.value === EstimatedType.NONE;
    const hasLowAge = age.includes(NO_LIMIT) || !age.length || age.find(item => item.includes('13,17'));
    return !noData && hasLowAge;
  });

  return {
    ageTipVisible,
  };
}

/**
 * ttp下受众过窄会阻塞创编的提示
 * writable 为 false 就是会阻塞创编
 */
export const usePropsTtpTipVisible = createSharedComposable(function usePropsTtpTipVisible() {
  const isWritable = computed(() => curEstiamteResult.value?.isWritable ?? true);
  const ttpCloverTipVisible = computed(() => !isWritable.value);

  return {
    ttpCloverTipVisible,
  };
});

/**
 * 受众范围太窄提示
 */
export function usePropsNarrowTipVisible(options: {
  adgroupData: Ref<AdgroupFormData>;
  estimateType: Ref<EstimatedType>;
}) {
  const { adgroupData, estimateType } = options;
  const { ttpCloverTipVisible } = usePropsTtpTipVisible();
  // todo 定向扩量tip
  const isTargetingExpansionAvailable = ref(false);
  const isTargetingExpansionON = computed(() => adgroupData.value.targetingExpansion.expansion_enabled);
  const isUseTargetExpansion = computed(() => isTargetingExpansionAvailable.value && isTargetingExpansionON.value);
  const isNarrow = computed(() => [EstimatedType.NARROW, EstimatedType.TOO_NARROW].includes(estimateType.value));
  const narrowTipVisible = computed(() => isNarrow.value && !ttpCloverTipVisible.value && !isUseTargetExpansion.value);

  return {
    narrowTipVisible,
  };
}

/**
 * 受众范围太宽提示
 */
export function usePropsBroadTipVisible(options: { estimateType: Ref<EstimatedType> }) {
  const { estimateType } = options;
  const broadTipVisible = computed(() => estimateType.value === EstimatedType.BROAD);

  return {
    broadTipVisible,
  };
}

export function usePropsImpressionTipVisible(options: {
  campaignData: Ref<CampaignFormData>;
  adgroupData: Ref<AdgroupFormData>;
  createMode: Ref<CreateMode>;
}) {
  const { campaignData, adgroupData, createMode } = options;
  const { isSelfAccount } = useStdAccountStore();
  const { isWebsite, isDailyBudget, isHasTikTok, isHasTopbuzz } = useStdAdgroupIs({
    campaignFormData: campaignData,
    adgroupFormData: adgroupData,
  });
  const { isConversion } = useStdObjectiveType({
    campaignFormData: campaignData,
  });
  const { isCreate } = useAdgroupMode({
    createMode,
  });
  const impressionTipVisible = computed(
    () =>
      isCreate.value &&
      isSelfAccount.value &&
      isConversion.value &&
      isWebsite.value &&
      isDailyBudget.value &&
      (isHasTikTok.value || isHasTopbuzz.value),
  );

  return {
    impressionTipVisible,
  };
}

/**
 * pangle retargeting提示
 */
export function usePropsPangleRetargetingTipVisible(options: {
  campaignData: Ref<CampaignFormData>;
  adgroupData: Ref<AdgroupFormData>;
}) {
  const { adgroupData, campaignData } = options;
  const { isHasPangle, isProductRetargeting } = useStdAdgroupIs({
    campaignFormData: campaignData,
    adgroupFormData: adgroupData,
  });

  const pangleRetargetingTipVisible = computed(() => isProductRetargeting.value && isHasPangle.value);

  return {
    pangleRetargetingTipVisible,
  };
}

export const usePropsTtpUnsupportedArea = createSharedComposable(function usePropsTtpUnsupportedArea() {
  const { state } = useStore();
  const areaData = state[StateEnum.areaData].data;
  const areaMap = computed(() => areaData?.value?.areaMap ?? {});

  const ttpUnsupportedArea = computed(
    () => curEstiamteResult.value?.ttpUnsupportedArea?.map(id => areaMap.value?.[id]?.translation || '') ?? [],
  );
  const cloverUnsupportedArea = computed(
    () => curEstiamteResult.value?.cloverUnsupportedArea?.map(id => areaMap.value?.[id]?.translation || '') ?? [],
  );

  const isHasTtpUnSupportArea = computed(() => ttpUnsupportedArea.value.length > 0);
  const isHasCloverUnSupportArea = computed(() => cloverUnsupportedArea.value.length > 0);

  return {
    ttpUnsupportedArea,
    cloverUnsupportedArea,
    isHasTtpUnSupportArea,
    isHasCloverUnSupportArea,
  };
});

export function useTtpCloverUnsupportedTips() {
  const { isHasTtpUnSupportArea, isHasCloverUnSupportArea } = usePropsTtpUnsupportedArea();

  // 生成 adgroup 层级 next 按钮左侧报错 popover 的提示
  const estimateLegalErrorKey = computed(() => {
    if (isHasTtpUnSupportArea.value && isHasCloverUnSupportArea.value) {
      return 'adgroup_check_error_clover_and_ttp_estimate_too_narrow';
    }
    if (isHasTtpUnSupportArea.value) {
      return 'audience_estimate_tips_too_narrow_with_ttp';
    }
    if (isHasCloverUnSupportArea.value) {
      return 'adgroup_check_error_clover_estimate_too_narrow';
    }
    return '';
  });

  // 生成受众预估挂件的报错提示
  const estimateRightBarErrorKey = computed(() => {
    if (isHasTtpUnSupportArea.value && isHasCloverUnSupportArea.value) {
      return 'audience_estimate_tips_can_not_deliver_ttp_and_clover';
    }
    if (isHasTtpUnSupportArea.value) {
      return 'audience_estimate_tips_can_not_deliver';
    }
    if (isHasCloverUnSupportArea.value) {
      return 'audience_estimate_tips_can_not_deliver_clover';
    }
    return '';
  });

  return {
    estimateRightBarErrorKey,
    estimateLegalErrorKey,
  };
}

export function usePropsEstimateFail(options: { estimateResult: Ref<AudienceEstimate> }) {
  const { estimateResult } = options;
  const estimateFail = computed(() => estimateResult.value.status === EstimateStatus.FAILED);

  return {
    estimateFail,
  };
}

export function usePropsEstimateCount(options: { estimateResult: Ref<AudienceEstimate> }) {
  const { estimateResult } = options;
  const { currencyOption } = useCurrencyOption();
  const estimateCount = computed(() => {
    const raw = estimateResult.value.userCount || '';
    if (raw[0] === '<') {
      const num = raw.slice(1).trim().split(',').join('');
      return `<${formatNumberByLangCode({
        value: num,
        precision: 0,
        platformLangCode: currencyOption.value?.langCode,
      })}`;
    } else if (raw?.includes?.('-')) {
      return raw
        .split('-')
        .map(item =>
          formatNumberByLangCode({
            value: item.split(',').join(''),
            precision: 0,
            platformLangCode: currencyOption.value?.langCode,
          }),
        )
        .join('-');
    } else {
      return raw;
    }
  });
  return {
    estimateCount,
  };
}

export function useDataSource(options: { campaignData: Ref<CampaignFormData>; adgroupData: Ref<AdgroupFormData> }) {
  const { campaignData, adgroupData } = options;
  const { opSysFilter } = useAdgroupIos14({
    campaignFormData: campaignData,
    adgroupFormData: adgroupData,
  });
  const { state } = useStore();
  const fields = computed(() => ({
    ...adgroupData.value,
    opSysFilter: opSysFilter.value,
    ecommType: campaignData.value.ecommType,
  }));
  // Todo Splittest ttp
  const estParams = computed(
    () =>
      ({
        data: fields.value,
        isSplitTest: false,
        withSignature: false,
        locationParams: {
          areaMap: state[StateEnum.areaData].data.value?.areaMap ?? {},
          isSplitTestBidding: false,
          isSplitTestCreative: false,
        },
      } as AuEstFormmater),
  );
  const audienceParams = computed(() => audienceRequstFormatter(estParams.value));
  const {
    data: auEstData,
    isLoading,
    retry,
    reset,
    cancel,
  } = useRequest(useAudienceEstimateApi, estParams.value, {
    manual: true,
  });
  const estimateData = computed(() => ({
    ...auEstData.value,
    status: EstimateStatus.LOADING,
    score: EstimatedType.NONE,
  }));
  const audienceData = useAudienceEstiamte({
    adgroupFormData: computed(() => estParams.value.data as AdgroupFormData),
    estimateData,
    params: computed(() => estParams.value.data),
  });

  const { isCLA, isPSA } = useStdAdgroupIs({
    adgroupFormData: adgroupData,
    campaignFormData: campaignData,
  });
  // CLA不支持受众预估
  const isAudienceEstimateDisabled = computed(() => isCLA.value && isPSA.value);
  const retryDataSource = debounce(() => {
    if (isAudienceEstimateDisabled.value) {
      cancel();
      reset();
    } else {
      retry(estParams.value);
    }
  }, 1000);

  onMounted(() => {
    retryDataSource();
  });

  watch(
    audienceParams,
    (newVal, oldVal) => {
      if (!isEqual(newVal, oldVal)) {
        retryDataSource();
      }
    },
    { deep: true },
  );

  const data = audienceData.estData;

  watch(data, () => {
    curEstiamteResult.value = audienceData.estData.value;
  });

  return {
    originData: estimateData,
    data,
    isLoading,
    retry,
  };
}

/**
 * Cla无受众预估提示
 */
export function usePropsTipVisible(options: {
  campaignData: Ref<CampaignFormData>;
  adgroupData: Ref<AdgroupFormData>;
}) {
  const { adgroupData, campaignData } = options;
  const { isCLA, isPSA } = useStdAdgroupIs({
    adgroupFormData: adgroupData,
    campaignFormData: campaignData,
  });
  const claTipVisible = computed(() => isCLA.value);
  const psaTipVisible = computed(() => isPSA.value);

  const noEstimatePrompt = computed(() => claTipVisible.value || psaTipVisible.value);

  /** 隐藏预估数量组件 */
  const noEstimateCount = computed(() => psaTipVisible.value);

  return {
    claTipVisible,
    psaTipVisible,
    noEstimatePrompt,
    noEstimateCount,
  };
}
